import { useCallback, useContext, useMemo } from 'react';
import MetricListsContext from '../../../../../../contexts/MetricListsContext';
import MetricsContext from '../../../../../../contexts/MetricsContext';
import DashboardsContext from '../../../../../../contexts/DashboardsContext';
import CardTypeCheckers from '../../../../../../types/cardTypeCheckers';
import { MetricListUsage } from '../types';

/**
 * Returns a list of metric list gadgets that use the given field via any of the metrics in the list
 */
const useGetFieldMetricListUsage = () => {
  const { metricLists } = useContext(MetricListsContext);
  const { allMetrics } = useContext(MetricsContext);
  const { allDashboards } = useContext(DashboardsContext);

  // metricListId -> dashboardId (first match)
  const metricListDashboardIdLookup = useMemo(() => {
    const map: Record<
      string,
      { dashboardId: string; dashboardName: string } | undefined
    > = {};
    allDashboards.forEach((d) => {
      d.canvas?.cards?.forEach((c) => {
        if (CardTypeCheckers.isMetricList(c)) {
          const mlId = c.content.metricListId;
          if (mlId && !map[mlId]) {
            map[mlId] = {
              dashboardId: d.id,
              dashboardName: d.name,
            };
          }
        }
      });
    });
    return map;
  }, [allDashboards]);
  const metricLookup = useMemo(() => {
    const map: Record<string, Metrics.NormalMetric | undefined> = {};
    allMetrics.forEach((m) => (map[m.id] = m));
    return map;
  }, [allMetrics]);

  const getFieldMetricListUsage = useCallback(
    (field: string, dataset: string): MetricListUsage[] => {
      return metricLists
        .filter((ml) => {
          // check any item in the list references a metric whose field matches
          return ml.list?.some(
            (item) =>
              metricLookup[item.metricId]?.field === field &&
              metricLookup[item.metricId]?.dataType === dataset,
          );
        })
        .map((ml) => ({
          ...ml,
          ...(metricListDashboardIdLookup[ml.id] || {
            dashboardId: '',
            dashboardName: '',
          }),
        }));
    },
    [metricListDashboardIdLookup, metricLists, metricLookup],
  );

  return getFieldMetricListUsage;
};

export default useGetFieldMetricListUsage;
